import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, X, Check, Loader2, MapPin, Send, ImagePlus, Image as ImageIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import VerificationModal from './VerificationModal';
import {
  compressImageToDataUrl,
  validateImageFile,
  MAX_IMAGES,
  ACCEPTED_TYPES,
  ACCEPTED_EXTENSIONS_LABEL,
} from '../utils/imageUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ServiceWizard = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [questionnaire, setQuestionnaire] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(-1); // -1 = landing page
  const [answers, setAnswers] = useState({});
  const [contactData, setContactData] = useState({ name: '', email: '', phone: '', location: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [otherText, setOtherText] = useState({});
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [images, setImages] = useState([]); // array of compressed data URLs
  const [imageProcessing, setImageProcessing] = useState(false);
  const [imageError, setImageError] = useState('');

  useEffect(() => {
    const fetchQuestionnaire = async () => {
      try {
        const res = await axios.get(`${API}/questionnaires/${slug}`);
        setQuestionnaire(res.data);
      } catch (e) {
        console.error('Error fetching questionnaire:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchQuestionnaire();
  }, [slug]);

  const totalSteps = questionnaire?.steps?.length || 0;
  const progress = totalSteps > 0 ? ((currentStep + 1) / totalSteps) * 100 : 0;

  const currentStepData = currentStep >= 0 && currentStep < totalSteps
    ? questionnaire.steps[currentStep]
    : null;

  const handleRadioSelect = (value) => {
    setAnswers(prev => ({ ...prev, [currentStep]: value }));
  };

  const handleCheckboxToggle = (value) => {
    const current = answers[currentStep] || [];
    const updated = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    setAnswers(prev => ({ ...prev, [currentStep]: updated }));
  };

  const handleOtherText = (stepIdx, text) => {
    setOtherText(prev => ({ ...prev, [stepIdx]: text }));
  };

  const handleImageSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = ''; // allow re-selecting the same file
    if (files.length === 0) return;

    setImageError('');
    const remaining = MAX_IMAGES - images.length;
    if (remaining <= 0) {
      setImageError(`You can upload a maximum of ${MAX_IMAGES} images.`);
      return;
    }
    const toProcess = files.slice(0, remaining);
    if (files.length > remaining) {
      setImageError(`Only the first ${remaining} image(s) were added (max ${MAX_IMAGES} total).`);
    }

    setImageProcessing(true);
    try {
      for (const file of toProcess) {
        try {
          validateImageFile(file);
          // eslint-disable-next-line no-await-in-loop
          const dataUrl = await compressImageToDataUrl(file);
          setImages(prev => [...prev, dataUrl]);
        } catch (err) {
          setImageError(err.message || 'Failed to process image.');
          break;
        }
      }
    } finally {
      setImageProcessing(false);
    }
  };

  const removeImage = (idx) => {
    setImages(prev => prev.filter((_, i) => i !== idx));
    setImageError('');
  };

  // Strict-enough RFC-5322-style email check. Rejects single words, missing
  // domain or TLD, trailing dots, spaces, etc.
  const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)+$/;
  const isValidEmail = (e) => EMAIL_REGEX.test((e || '').trim());

  const canContinue = () => {
    if (!currentStepData) return true;
    if (currentStepData.type === 'contact_form') {
      return Boolean(contactData.name && contactData.name.trim()) && isValidEmail(contactData.email);
    }
    const answer = answers[currentStep];
    if (currentStepData.type === 'checkbox') return answer && answer.length > 0;
    return !!answer;
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    } else if (currentStep === 0) {
      setCurrentStep(-1);
    }
  };

  const handleSubmit = async () => {
    // Check if user is logged in
    if (!user) {
      alert('Please log in to submit a service request');
      login();
      return;
    }

    // Check verification status
    try {
      const verifyRes = await axios.get(`${API}/verification/status`, { withCredentials: true });
      setVerificationStatus(verifyRes.data);
      
      if (!verifyRes.data.email_verified || !verifyRes.data.phone_verified) {
        // Show verification modal
        setVerificationModalOpen(true);
        return;
      }
    } catch (e) {
      console.error('Error checking verification:', e);
      alert('Please log in to submit a service request');
      login();
      return;
    }

    // Proceed with submission
    setSubmitting(true);
    try {
      // Build answers object
      const formattedAnswers = {};
      questionnaire.steps.forEach((step, idx) => {
        if (step.type !== 'contact_form') {
          let answer = answers[idx];
          if (answer === '__other__' && otherText[idx]) {
            answer = otherText[idx];
          }
          formattedAnswers[step.question] = answer;
        }
      });

      const res = await axios.post(
        `${API}/requests/questionnaire`,
        {
          service_slug: questionnaire.service_slug,
          service_name: questionnaire.service_name,
          answers: formattedAnswers,
          contact: contactData,
          images,
        },
        { withCredentials: true }
      );
      
      // Navigate to results page with context
      navigate(`/results/${questionnaire.service_slug}`, {
        replace: true,
        state: {
          service_name: questionnaire.service_name,
          contact: contactData,
          request_id: res.data.id
        }
      });
    } catch (e) {
      console.error('Submit error:', e);
      if (e.response?.status === 403) {
        // Verification required
        alert(e.response.data.detail);
        setVerificationModalOpen(true);
      } else {
        alert('Failed to submit request. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerificationComplete = () => {
    // Retry submission after verification
    handleSubmit();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 size={40} className="animate-spin text-[#37b34a]" />
      </div>
    );
  }

  if (!questionnaire) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <p className="text-gray-500 text-lg mb-4">Service not found</p>
          <button onClick={() => navigate('/')} className="text-[#37b34a] font-semibold hover:underline">
            Back to home
          </button>
        </div>
      </div>
    );
  }

  // Landing page
  if (currentStep === -1) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="relative h-[280px] md:h-[340px] overflow-hidden">
          <img
            src={questionnaire.hero_image}
            alt={questionnaire.service_name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40" />
          <button
            onClick={() => navigate('/')}
            className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors z-10"
          >
            <ArrowLeft size={20} className="text-gray-700" />
          </button>
          <div className="absolute inset-0 flex items-center justify-center">
            <h1 className="text-3xl md:text-4xl font-bold text-white text-center px-4">
              Find {questionnaire.service_name} near me
            </h1>
          </div>
        </div>

        <div className="max-w-md mx-auto -mt-10 relative z-10 px-4 pb-10">
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Where do you need {questionnaire.service_name}?
            </label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <MapPin size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Enter your area or postal code"
                  value={contactData.location}
                  onChange={(e) => setContactData(prev => ({ ...prev, location: e.target.value }))}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#37b34a] transition-colors"
                />
              </div>
              <button
                onClick={() => setCurrentStep(0)}
                className="bg-[#4A6CF7] hover:bg-[#3d5bd6] text-white font-semibold px-6 py-3 rounded-xl transition-colors"
              >
                Go
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Question steps
  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center pt-8 md:pt-16 px-4">
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* Progress bar */}
        <div className="px-6 pt-5 pb-3 flex items-center gap-3">
          <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#4A6CF7] rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <button
            onClick={() => navigate('/')}
            className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 pb-6">
          {/* Question */}
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 text-center mb-1">
            {currentStepData.question}
          </h2>
          {currentStepData.subtitle && (
            <p className="text-gray-400 text-sm text-center mb-4">{currentStepData.subtitle}</p>
          )}
          {currentStep === 0 && questionnaire.tagline && (
            <p className="text-gray-400 text-sm text-center mb-4">{questionnaire.tagline}</p>
          )}

          {/* Options */}
          <div className="mt-5 space-y-0 border border-gray-200 rounded-xl overflow-hidden">
            {currentStepData.type === 'radio' && currentStepData.options?.map((option, idx) => (
              <div
                key={idx}
                onClick={() => handleRadioSelect(option)}
                className={`flex items-center gap-3 px-4 py-3.5 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                  answers[currentStep] === option ? 'bg-blue-50' : ''
                }`}
              >
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                  answers[currentStep] === option ? 'border-[#4A6CF7] bg-[#4A6CF7]' : 'border-gray-300'
                }`}>
                  {answers[currentStep] === option && <div className="w-2 h-2 rounded-full bg-white" />}
                </div>
                <span className="text-gray-700 text-sm">{option}</span>
              </div>
            ))}

            {currentStepData.type === 'checkbox' && currentStepData.options?.map((option, idx) => {
              const checked = (answers[currentStep] || []).includes(option);
              return (
                <div
                  key={idx}
                  onClick={() => handleCheckboxToggle(option)}
                  className={`flex items-center gap-3 px-4 py-3.5 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                    checked ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    checked ? 'border-[#4A6CF7] bg-[#4A6CF7]' : 'border-gray-300'
                  }`}>
                    {checked && <Check size={12} className="text-white" />}
                  </div>
                  <span className="text-gray-700 text-sm">{option}</span>
                </div>
              );
            })}

            {currentStepData.has_other && (
              <div
                className={`flex items-center gap-3 px-4 py-3.5 cursor-pointer hover:bg-gray-50 transition-colors ${
                  (currentStepData.type === 'radio' && answers[currentStep] === '__other__') ||
                  (currentStepData.type === 'checkbox' && (answers[currentStep] || []).includes('__other__'))
                    ? 'bg-blue-50' : ''
                }`}
                onClick={() => {
                  if (currentStepData.type === 'radio') handleRadioSelect('__other__');
                  else if (!(answers[currentStep] || []).includes('__other__')) handleCheckboxToggle('__other__');
                }}
              >
                {currentStepData.type === 'radio' ? (
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    answers[currentStep] === '__other__' ? 'border-[#4A6CF7] bg-[#4A6CF7]' : 'border-gray-300'
                  }`}>
                    {answers[currentStep] === '__other__' && <div className="w-2 h-2 rounded-full bg-white" />}
                  </div>
                ) : (
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    (answers[currentStep] || []).includes('__other__') ? 'border-[#4A6CF7] bg-[#4A6CF7]' : 'border-gray-300'
                  }`}>
                    {(answers[currentStep] || []).includes('__other__') && <Check size={12} className="text-white" />}
                  </div>
                )}
                <input
                  type="text"
                  placeholder="Other"
                  value={otherText[currentStep] || ''}
                  onChange={(e) => handleOtherText(currentStep, e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onFocus={() => {
                    if (currentStepData.type === 'radio') handleRadioSelect('__other__');
                    else if (!(answers[currentStep] || []).includes('__other__')) handleCheckboxToggle('__other__');
                  }}
                  className="flex-1 border-none outline-none text-sm text-gray-700 bg-transparent placeholder-gray-400"
                />
              </div>
            )}

            {currentStepData.type === 'contact_form' && (
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your name *</label>
                  <input
                    value={contactData.name}
                    onChange={(e) => setContactData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="John Doe"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#37b34a] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    value={contactData.email}
                    onChange={(e) => setContactData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="john@example.com"
                    autoComplete="email"
                    inputMode="email"
                    aria-invalid={contactData.email.length > 0 && !isValidEmail(contactData.email)}
                    data-testid="wizard-email-input"
                    className={`w-full px-4 py-3 border rounded-xl text-sm focus:outline-none transition-colors ${
                      contactData.email.length > 0 && !isValidEmail(contactData.email)
                        ? 'border-red-300 focus:border-red-500 bg-red-50/30'
                        : 'border-gray-200 focus:border-[#37b34a]'
                    }`}
                  />
                  {contactData.email.length > 0 && !isValidEmail(contactData.email) && (
                    <p className="text-xs text-red-600 mt-1.5" data-testid="wizard-email-error">
                      Please enter a valid email address (e.g. name@example.com).
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    value={contactData.phone}
                    onChange={(e) => setContactData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="(021) 555-1234"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#37b34a] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <input
                    value={contactData.location}
                    onChange={(e) => setContactData(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="Your area or postal code"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#37b34a] transition-colors"
                  />
                </div>

                {/* Image uploads */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Add photos <span className="text-gray-400 font-normal">(optional, up to {MAX_IMAGES})</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Help pros quote accurately — {ACCEPTED_EXTENSIONS_LABEL}, max 5&nbsp;MB each.
                  </p>

                  {images.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mb-3" data-testid="image-previews">
                      {images.map((src, idx) => (
                        <div key={idx} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                          <img src={src} alt={`Upload ${idx + 1}`} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeImage(idx)}
                            data-testid={`remove-image-${idx}`}
                            className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white hover:bg-black/80 flex items-center justify-center transition-colors"
                            aria-label="Remove image"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {images.length < MAX_IMAGES && (
                    <label
                      className={`flex items-center justify-center gap-2 w-full py-3 px-4 border-2 border-dashed rounded-xl text-sm font-medium cursor-pointer transition-colors ${
                        imageProcessing
                          ? 'border-gray-200 text-gray-400 cursor-wait'
                          : 'border-gray-300 text-gray-600 hover:border-[#37b34a] hover:text-[#37b34a]'
                      }`}
                      data-testid="image-upload-trigger"
                    >
                      {imageProcessing ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <ImagePlus size={16} />
                      )}
                      {imageProcessing
                        ? 'Processing…'
                        : images.length === 0
                          ? 'Add photos'
                          : `Add ${MAX_IMAGES - images.length} more`}
                      <input
                        type="file"
                        accept={ACCEPTED_TYPES.join(',')}
                        multiple
                        onChange={handleImageSelect}
                        disabled={imageProcessing}
                        className="hidden"
                        data-testid="image-upload-input"
                      />
                    </label>
                  )}

                  {imageError && (
                    <p className="text-xs text-red-600 mt-2" data-testid="image-upload-error">{imageError}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-6">
            <button
              onClick={handleBack}
              className="px-5 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Back
            </button>
            <button
              onClick={handleNext}
              disabled={!canContinue() || submitting}
              className="px-6 py-2.5 text-sm font-semibold text-white bg-[#4A6CF7] hover:bg-[#3d5bd6] disabled:bg-gray-300 rounded-lg transition-colors flex items-center gap-2"
            >
              {submitting ? (
                <Loader2 size={16} className="animate-spin" />
              ) : currentStep === totalSteps - 1 ? (
                <><Send size={14} /> Submit</>
              ) : (
                'Continue'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Verification Modal */}
      <VerificationModal
        isOpen={verificationModalOpen}
        onClose={() => setVerificationModalOpen(false)}
        onVerificationComplete={handleVerificationComplete}
      />
    </div>
  );
};

export default ServiceWizard;
